import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Snackbar,
  TextField
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { STRINGS } from 'src/commons';
import ImagePicker from 'src/components/imagePicker';
import { updateAdminProfiledAPI } from 'src/api';
import { loaderReducerAction } from 'src/redux/reducers/loader';
import { commonReducerAction } from 'src/redux/reducers/common';
import PhoneInput from 'react-phone-input-2';
import { getMobileNumber } from 'src/utils/mobileNumberValidator';

const AccountProfileDetails = () => {
  const dispatch = useDispatch();
  const { startLoading, stopLoading } = loaderReducerAction;
  const { updateUserCredentials } = commonReducerAction;
  const { user } = useSelector((state) => state.common);
  const [imageFile, setImageFile] = useState(user?.profile_pic);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
    severity: ''
  });

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbarState({
      ...snackbarState,
      open: false
    });
  };
        
  return (
    <Card>
      <CardHeader title="Profile" />
      <Divider />
      <CardContent>
        <Formik
          initialValues={{
            fullName: user?.full_name,
            email: user?.email,
            phone: user?.phone,
            callingCode: user?.calling_code,
            profilePic: user?.profile_pic
          }}
          validationSchema={Yup.object().shape({
            fullName: Yup.string().max(20).required(STRINGS.FULL_NAME_REQUIRED),
            phone: Yup.string().optional()
          })}
          onSubmit={async (values) => {
            try {
              const formData = new FormData();
              formData.append('full_name', values.fullName);
              formData.append('device_type', 0);

              if (values?.phone && values?.callingCode) {
                formData.append('phone', values?.phone);
                formData.append('calling_code', values?.callingCode);
              }

              if (values?.logo) {
                formData.append('profile_pic', values.logo);
              }
              dispatch(startLoading());
              return updateAdminProfiledAPI(user, formData)
                .then((response) => {
                  const isError = response.status !== 200;
                  const message = isError
                    ? response?.message
                    : 'Profile Updated Successfully';
                  setSnackbarState({
                    open: true,
                    message: message,
                    severity: isError ? 'error' : 'success'
                  });
                  if (!isError) {
                    const updatedUser = response?.data;
                    if (!updatedUser?.all_gyms) {
                      updatedUser.all_gyms = user?.all_gyms;
                    }
                    dispatch(updateUserCredentials(updatedUser));
                  }
                })
                .catch((err) => {
                  throw err;
                })
                .finally(() => dispatch(stopLoading()));
            } catch (err) {
              dispatch(stopLoading());
              setSnackbarState({
                open: true,
                message: err.message,
                severity: 'error'
              });
            }
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            touched,
            values
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="relative">
                <input
                  label="Logo"
                  type="file"
                  accept="image/*"
                  className="hide"
                  id="logo"
                  name="logo"
                  onBlur={handleBlur}
                  onChange={(event) => {
                    setFieldValue('logo', event?.currentTarget?.files[0]);

                    const file = event?.currentTarget?.files[0];
                    if (file) {
                      const fileImage = new FileReader();
                      fileImage.readAsDataURL(file);
                      fileImage.onload = () => {
                        setImageFile(fileImage.result);
                      };
                    }
                  }}
                />
                <label for="logo" className="absolute">
                  <ImagePicker
                    pickedImage={imageFile}
                    setImage={setImageFile}
                    containerClass="borderRadius"
                    placeholder="Upload logo"
                    hideCancel
                  />
                </label>
              </div>

              <TextField
                error={Boolean(touched.fullName && errors.fullName)}
                helperText={touched.fullName && errors.fullName}
                fullWidth
                label={STRINGS.FULL_NAME}
                name="fullName"
                margin="normal"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.fullName}
                variant="outlined"
              />

              <TextField
                fullWidth
                label={STRINGS.EMAIL_ADDRESS}
                name="email"
                disabled
                value={values.email}
                margin="normal"
                variant="outlined"
                InputLabelProps={{
                  shrink: true
                }}
              />

              <Box sx={{ marginBottom: '8px' }}>
                <PhoneInput
                  country={'us'}
                  placeholder="Mobile Number"
                  inputStyle={{
                    height: '3.5rem',
                    width: '100%',
                    border:
                      touched.phone && errors?.phone
                        ? '1px solid red'
                        : '1px solid rgba(0, 0, 0, 0.23)',
                    marginTop: '16px'
                  }}
                  buttonClass={
                    touched.phone && errors?.phone
                      ? 'errorDropdown'
                      : 'flagDropdown'
                  }
                  value={values?.phone}
                  name="phone"
                  onChange={(value, country) => {
                    setFieldValue('phone', value);
                    setFieldValue('callingCode', `+${country?.dialCode}`);
                  }}
                  inputProps={{
                    required: true
                  }}
                  searchPlaceholder="Search"
                  enableSearch
                  countryCodeEditable={false}
                />
                {touched?.phone && errors?.phone && (
                  <p
                    style={{
                      color: 'red',
                      fontFamily: 'Roboto',
                      fontSize: '0.8rem',
                      marginLeft: '15px',
                      marginTop: '5px'
                    }}
                  >
                    {errors?.phone}
                  </p>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  p: 2
                }}
              >
                <Button
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {STRINGS.SAVE_DETAILS}
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </CardContent>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbarState.open}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <div>
          <Alert onClose={handleClose} severity={snackbarState.severity}>
            {snackbarState.message}
          </Alert>
        </div>
      </Snackbar>
    </Card>
  );
};

export default AccountProfileDetails;
